import styled from '@emotion/styled'
import type { FC, PropsWithChildren } from 'react'
import { useState } from 'react'

import HeroBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'
import CopyBase, * as Copy from '@segment/matcha/recipes/CopyText'

import EmailCaptureForm from '@segment/templates/Forms/EmailCaptureForm'

import heroPng2x from './assets/Hero@2x.png'
import heroWebp from './assets/Hero.webp'
import heroWebp2x from './assets/Hero@2x.webp'

const copy = {
  title: <>The Growth Report&nbsp;2022</>,
  description: `Brought to you by Twilio Segment`,
}

const form = {
  customFormIdentifier: `growth-report-2022`,
  additionalTrackEvent: `Content Downloaded`,
  buttonText: `Download the Growth Report`,
  buttonUrl: `https://segment.com/pdfs/Twilio-Segment-Growth-Report-2022.pdf`,
  overrideConsent: true,
}

const Hero: FC<PropsWithChildren<unknown>> = () => {
  const [isSubmitted, setIsSubmitted] = useState(false)

  return (
    <Wrapper>
      <BackgroundPattern />
      <StyledHeroBase variant="2Col">
        <HeroRecipe.Copy>
          <CopyBase>
            <Copy.Heading tag="h1" variant="h1">
              {copy.title}
            </Copy.Heading>
            <Copy.Description tag="p" variant="pExtraLarge">
              {copy.description}
            </Copy.Description>
          </CopyBase>
          <FormWrapper variant={isSubmitted ? `wide` : `narrow`}>
            <EmailCaptureForm
              resourceType="report"
              customFormIdentifier={form.customFormIdentifier}
              additionalTrackEvent={form.additionalTrackEvent}
              overrideConsent={form.overrideConsent}
              buttonText={form.buttonText}
              successButtonText={form.buttonText}
              buttonUrl={form.buttonUrl}
              onSubmit={() => setIsSubmitted(true)}
              enableJobTitleField={true}
            />
          </FormWrapper>
        </HeroRecipe.Copy>
        <HeroRecipe.Contents>
          <picture>
            <source type="image/webp" srcSet={`${heroWebp}, ${heroWebp2x} 2x`} />
            <HeroRecipe.Image src={heroPng2x} width="460" height="460" alt="Illustration: The Growth Report" />
          </picture>
        </HeroRecipe.Contents>
      </StyledHeroBase>
    </Wrapper>
  )
}

const Wrapper = styled.header`
  background-color: ${({ theme }) => theme.palette.brand.segmentGreen[100]};
  position: relative;
`

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-image: radial-gradient(${({ theme }) => theme.palette.brand.segmentGreen[400]} 1px, transparent 0);
  mask-image: linear-gradient(to right, transparent 20%, black);
  background-size: 18px 18px;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    background-image: radial-gradient(${({ theme }) => theme.palette.brand.segmentGreen[400]} 2px, transparent 0);
    background-size: 36px 36px;
  }
`

const StyledHeroBase = styled(HeroBase)`
  position: relative;
`

interface FormWrapperProps {
  variant: `wide` | `narrow`
}

const FormWrapper = styled.div<FormWrapperProps>`
  max-width: ${({ variant }) => (variant === `narrow` ? `417px` : `unset`)};
  margin-top: 38px;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    margin-top: 48px;
  }
`

export default Hero
